import {
  get,
  set,
  filter,
  some,
  includes,
  forEach,
  size,
  uniqueId,
  memoize,
  reduce,
  toNumber,
  isNaN,
} from 'lodash';

import { TYPES } from 'constants/merchandising';
import Log from 'utils/Log';

import {
  FILTER_CONFIGURATIONS,
  COLLECTION_PAGE_FILTER_IDENTIFIERS,
  FILTER_IDENTIFIERS,
  APPEARANCES,
  MATCH_TYPES,
  SIZE_FILTER_GROUPINGS,
} from './constants';

export {
  APPEARANCES,
  MATCH_TYPES,
  FILTER_IDENTIFIERS,
  COLLECTION_PAGE_FILTER_IDENTIFIERS,
  SIZE_FILTER_GROUPINGS,
};

const ENABLE_CACHE = true;

class Filters {
  /**
   * This method returns a unique cacheKey unless cache is enabled and a cacheKey has been provided
   */
  static _resolveCache = ({ cacheKey }) => {
    if (!ENABLE_CACHE || !cacheKey) {
      return uniqueId();
    }

    return cacheKey;
  };

  /**
   * This method takes a list of items and returns items that match a single specified filterItem's configuration. To execute many filters instead of one, see executeFilters().
   */
  static _executeFilter = ({ items, filterItem, cacheKey }) => {
    Log.debug(`_executeFilter - uncached (${cacheKey})`);

    if (!filterItem) {
      throw new Error(`_executeFilter - missing filterItem (${JSON.stringify(filterItem)})`);
    }

    const { configuration, values } = filterItem;

    if (!configuration) {
      throw new Error(
        `_executeFilter - missing filterItem.configuration (${JSON.stringify(filterItem)})`
      );
    }

    const compare = (field, value, item) => {
      if (configuration.matchType === MATCH_TYPES.EQUALITY) {
        return field === value;
      }

      if (configuration.matchType === MATCH_TYPES.INCLUDES) {
        return includes(field, value);
      }

      if (configuration.matchType === MATCH_TYPES.SIZE_AVAILABILITY) {
        return !!item.sizes[value] && item.sizes[value].available;
      }

      throw new Error(
        `_executeFilter - unknown filterItem.configuration.matchType (${JSON.stringify(
          filterItem
        )})`
      );
    };

    const filteredSet = filter(items, item => {
      const field = get(item, configuration.attributeField);

      // An item is considered a match if the value of its attributeField matches at least one of the filterItem's values
      const isMatch = some(values, value => compare(field, value, item));

      return isMatch;
    });

    return filteredSet;
  };

  static executeFilter = memoize(Filters._executeFilter, Filters._resolveCache);

  /**
   * This method takes a list of items and returns items that match the many specified filterItems' configurations.
   */
  static _executeFilters = ({ items, filterItems, cacheKey }) => {
    Log.debug(`_executeFilters - uncached (${cacheKey})`);

    return reduce(
      filterItems,
      (resultSet, filterItem) =>
        Filters.executeFilter({
          items: resultSet,
          filterItem,
        }),
      items
    );
  };

  static executeFilters = memoize(Filters._executeFilters, Filters._resolveCache);

  /**
   * This method returns all possible filter options that relate to the specified filterIdentifier and specified items
   */
  static _enumeratePossibleFilterOptions = ({ items, filterConfiguration, cacheKey }) => {
    Log.debug(`_enumeratePossibleFilterOptions - uncached (${cacheKey})`);

    const possibleOptions = {};

    if (!filterConfiguration) {
      throw new Error(
        `_enumeratePossibleFilterOptions - missing filterConfiguration (${JSON.stringify(
          filterConfiguration
        )})`
      );
    }

    forEach(items, item => {
      const value = get(item, filterConfiguration.attributeField);
      const productType = item?.type;

      if (!value || productType === TYPES.GIFT_CARDS) {
        return;
      }

      if (filterConfiguration.matchType === MATCH_TYPES.EQUALITY) {
        set(possibleOptions, value, {
          option: value,
        });
      }

      if (filterConfiguration.matchType === MATCH_TYPES.INCLUDES) {
        forEach(value, arrayElement => {
          set(possibleOptions, arrayElement, {
            option: arrayElement,
          });
        });
      }

      if (filterConfiguration.matchType === MATCH_TYPES.SIZE_AVAILABILITY) {
        forEach(value, arrayElement => {
          const isNumeric = !isNaN(parseFloat(arrayElement));
          const isUnisizeShoe = arrayElement.split('-').length === 2;
          const isUnisizeApparel = arrayElement.split('-').length === 3;

          let grouping;
          if (isUnisizeApparel) {
            grouping = SIZE_FILTER_GROUPINGS.UNISEX_APPAREL;
          } else if (isUnisizeShoe) {
            grouping = SIZE_FILTER_GROUPINGS.UNISEX_SHOE;
          } else if (isNumeric) {
            grouping = SIZE_FILTER_GROUPINGS.SHOE;
          } else {
            grouping = SIZE_FILTER_GROUPINGS.APPAREL;
          }

          set(possibleOptions, arrayElement, {
            label: item.sizes[arrayElement].value,
            option: arrayElement,
            grouping,
          });
        });
      }
    });

    const optionsArray = Filters.mapPossibleFilterOptionsToArray(possibleOptions);

    const footwearSortedOptions = Filters.sortPossibleFilterOptionsArray(
      optionsArray.filter(option => option?.grouping !== SIZE_FILTER_GROUPINGS.APPAREL),
      filterConfiguration.matchType
    );

    const apparelSortedOptions = Filters.sortPossibleFilterOptionsArray(
      optionsArray.filter(option => option?.grouping == SIZE_FILTER_GROUPINGS.APPAREL),
      filterConfiguration.matchType
    );

    // give precedence of footwear over apparel
    return [...footwearSortedOptions, ...apparelSortedOptions];
  };

  static enumeratePossibleFilterOptions = memoize(
    Filters._enumeratePossibleFilterOptions,
    Filters._resolveCache
  );

  /**
   * This method returns the configuration that relates to the specified filterIdentifier
   */
  static getFilterConfiguration = ({ filterIdentifier }) =>
    get(FILTER_CONFIGURATIONS, [filterIdentifier]);

  /**
   * This method returns the filter identifiers that are relevant for a set of items. Filters are considered relevant if they have at least two possible options in the provided list of items.
   * For example, if the list of items is comprised entirely of wool shoes, filtering by material is not relevant for a user because it wouldn't filter out any items. On the other hand, if the list of items had many different hues, filtering by hue would be relevant for a user, because it would narrow down the list of items.
   */
  static _determineRelevantFilterIdentifiers = ({ items, filterIdentifiers, cacheKey }) => {
    Log.debug(`_determineRelevantFilterIdentifiers - uncached (${cacheKey})`);

    return reduce(
      filterIdentifiers,
      (relevantFilters, filterIdentifier) => {
        const filterConfiguration = Filters.getFilterConfiguration({ filterIdentifier });

        const possibleFilterOptions = Filters._enumeratePossibleFilterOptions({
          items,
          filterConfiguration,
        });

        if (size(possibleFilterOptions) > 1) {
          relevantFilters.push(filterIdentifier);
        }

        return relevantFilters;
      },
      []
    );
  };

  static determineRelevantFilterIdentifiers = memoize(
    Filters._determineRelevantFilterIdentifiers,
    Filters._resolveCache
  );

  /**
   * This method returns the filters that are relevant to a collection page based on a list of items.
   */
  static _determineRelevantCollectionPageFilterIdentifiers = ({ items, cacheKey }) => {
    Log.debug(`_determineRelevantCollectionPageFilterIdentifiers - uncached (${cacheKey})`);

    return Filters._determineRelevantFilterIdentifiers({
      items,
      filterIdentifiers: COLLECTION_PAGE_FILTER_IDENTIFIERS,
    });
  };

  static determineRelevantCollectionPageFilterIdentifiers = memoize(
    Filters._determineRelevantCollectionPageFilterIdentifiers,
    Filters._resolveCache
  );

  static mapPossibleFilterOptionsToArray = options => {
    return Object.values(options);
  };

  static sortPossibleFilterOptionsArray = (optionsArray = [], matchType) => {
    if (matchType !== MATCH_TYPES.SIZE_AVAILABILITY) {
      return optionsArray;
    }

    const alpha = [];
    const numeric = [];
    const coerce = str => {
      const number = toNumber(
        str
          .replace(/^-/, '') // strip leading dashes
          .replace('-', '.') // replace dashes with decimals
      );

      return number === 0 ? NaN : number; // toNumber('') === 0
    };

    optionsArray.forEach(option => {
      const value = coerce(option.option);

      if (isNaN(value)) {
        alpha.push(option);
      } else {
        numeric.push(option);
      }
    });

    numeric.sort((a, b) => {
      const aValue = coerce(a.option);
      const bValue = coerce(b.option);

      return aValue - bValue;
    });

    return [...numeric, ...alpha];
  };
}

export default Filters;
